import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import { cn } from '@/lib/cn'

const STORAGE_KEY = 'velox.split_bottom_px_v3'
/** 20% taller than prior 225px default. */
const DEFAULT_BOTTOM_PX = 270
const MIN_TOP_PX = 180
const MIN_BOTTOM_PX = 120

function readStoredBottom(): number | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const n = Number(raw)
    return Number.isFinite(n) ? n : null
  } catch {
    return null
  }
}

export interface ResizableSplitPaneProps {
  top: ReactNode
  bottom: ReactNode
  className?: string
  /** Initial bottom panel height in px (overridden by localStorage when present). */
  defaultBottomPx?: number
}

/**
 * Vertical split: top (chart workspace) / bottom (positions blotter).
 * Draggable handle; persists height; defaults ~50% taller than old fixed panel.
 */
export function ResizableSplitPane({
  top,
  bottom,
  className,
  defaultBottomPx = DEFAULT_BOTTOM_PX,
}: ResizableSplitPaneProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const [bottomPx, setBottomPx] = useState(
    () => readStoredBottom() ?? defaultBottomPx,
  )
  const [dragging, setDragging] = useState(false)

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(Math.round(bottomPx)))
    } catch {
      /* ignore */
    }
  }, [bottomPx])

  const clampBottom = useCallback((next: number, total: number) => {
    const maxBottom = Math.max(MIN_BOTTOM_PX, total - MIN_TOP_PX)
    return Math.min(maxBottom, Math.max(MIN_BOTTOM_PX, next))
  }, [])

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.preventDefault()
    const root = rootRef.current
    if (!root) return
    const rect = root.getBoundingClientRect()
    const startY = e.clientY
    const startBottom = bottomPx
    setDragging(true)

    const target = e.currentTarget
    target.setPointerCapture(e.pointerId)

    const onMove = (ev: PointerEvent) => {
      const delta = startY - ev.clientY
      setBottomPx(clampBottom(startBottom + delta, rect.height))
    }

    const onUp = (ev: PointerEvent) => {
      setDragging(false)
      target.releasePointerCapture(ev.pointerId)
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  const onKeyResize = (e: React.KeyboardEvent) => {
    const root = rootRef.current
    if (!root) return
    const total = root.getBoundingClientRect().height
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setBottomPx((b) => clampBottom(b + 16, total))
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setBottomPx((b) => clampBottom(b - 16, total))
    } else if (e.key === 'Home') {
      e.preventDefault()
      setBottomPx(clampBottom(MIN_BOTTOM_PX, total))
    } else if (e.key === 'End') {
      e.preventDefault()
      setBottomPx(clampBottom(DEFAULT_BOTTOM_PX * 1.6, total))
    }
  }

  return (
    <div
      ref={rootRef}
      className={cn('flex min-h-0 flex-1 flex-col', className)}
    >
      <div className="min-h-0 flex-1 overflow-hidden">{top}</div>

      <div
        role="separator"
        aria-orientation="horizontal"
        aria-valuenow={Math.round(bottomPx)}
        aria-valuemin={MIN_BOTTOM_PX}
        tabIndex={0}
        onPointerDown={onPointerDown}
        onKeyDown={onKeyResize}
        className={cn(
          'group relative z-10 flex h-1.5 shrink-0 cursor-row-resize items-center justify-center',
          'border-y border-terminal-border/40 bg-terminal-surface',
          'hover:bg-terminal-elevated focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent',
          dragging && 'bg-accent/30',
        )}
      >
        <span
          className={cn(
            'h-0.5 w-10 rounded-full bg-terminal-border transition-colors',
            'group-hover:bg-accent',
            dragging && 'bg-accent',
          )}
        />
      </div>

      <div
        className="shrink-0 overflow-hidden bg-terminal-bg"
        style={{ height: bottomPx }}
      >
        <div className="h-full p-1.5">{bottom}</div>
      </div>
    </div>
  )
}
